/**
 * Static plan data used as fallback when the API is unreachable
 * (e.g. during react-snap pre-rendering).
 *
 * Keep in sync with SubscriptionPlan.PLANS in
 * mostlyagent/api/services/subscription_service.py
 */
import { PlanConfig } from '../services/subscriptionService';

/** Fallback only: the pricing page reads live hosting rates from each plan's credit_usage_rates. */
export const STATIC_HOSTING_CREDIT_RATES = {
  cpu: 3.063,
  ram: 0.768,
  disk: 0.00807,
} as const;

const SHARED_FEATURES: string[] = [
  'Tech Lead Agent',
  'Frontend Agent',
  'Backend Agent',
  'System Architect Agent',
  'MicroService Architecture',
  'Docker Agent',
  'Designer Agent',
  'Unittest Agent',
  'Integration Testing Agent',
  'Visual Testing Agent',
  'Kubernetes Cluster Stage Deployment',
  'Kubernetes Cluster Prod Deployment',
  'Realtime Visual Replay',
  'Supporting Any Programming Language',
  'Direct K8s Cluster Dashboard Access',
  'VSCode Editor',
  'Auto Scaling (Horizontal)',
  'Mobile Phone Interface Support',
  'Assign Tasks to Human',
  'Automatic Retry Agent',
  'Self-correcting agent loops',
  'Custom Agent',
];

export const STATIC_PLANS: Record<string, PlanConfig> = {
  free: {
    name: 'Free',
    monthly_price: 0,
    annual_price: 0,
    monthly_credits: 5,
    monthly_total_credits: 50,
    daily_drip_credits: 0,
    free_credit_schedule: {
      upfront: 5, boost_days: 4, boost_daily: 5, daily: 1, tail_days: 25, cycle_days: 30, estimated_total: 50,
      expires_each_cycle: true,
    },
    max_users: 1,
    topup_price_per_credit: 0.014593,
    topup_price_per_credit_inr: 1.4593,
    topup_price_per_credit_apple: 0.020846,
    features: [...SHARED_FEATURES],
  },
  standard: {
    name: 'Standard',
    monthly_price: 4.49,
    annual_price: 48.49,
    monthly_credits: 200,
    monthly_total_credits: 400,
    daily_drip_credits: 6.9,
    max_users: -1,
    topup_price_per_credit: 0.011225,
    topup_price_per_credit_inr: 1.1225,
    topup_price_per_credit_apple: 0.016036,
    monthly_price_inr: 449,
    annual_price_inr: 4849,
    apple_product_id_monthly: 'com.app.ai8080.sub.standard.monthly.v1',
    apple_product_id_annual: 'com.app.ai8080.sub.standard.annual.v1',
    apple_price_monthly: 1.49,
    apple_price_annual: 15.49,
    features: [...SHARED_FEATURES, 'BYOK Support'],
  },
  standard_india: {
    name: 'Standard',
    monthly_price: 1.99,
    annual_price: 21.49,
    monthly_credits: 100,
    monthly_total_credits: 200,
    daily_drip_credits: 3.45,
    max_users: -1,
    topup_price_per_credit: 0.011225,
    topup_price_per_credit_inr: 1.1225,
    topup_price_per_credit_apple: 0.016036,
    monthly_price_inr: 199,
    annual_price_inr: 2149,
    features: [...SHARED_FEATURES, 'BYOK Support'],
  },
  byok: {
    name: 'BYOK',
    monthly_price: 5,
    annual_price: 54,
    monthly_credits: -1,
    daily_drip_credits: 0,
    max_users: -1,
    topup_price_per_credit: 0.005,
    topup_price_per_credit_inr: 0.449,
    topup_price_per_credit_apple: 0.007143,
    monthly_price_inr: 449,
    annual_price_inr: 4838,
    apple_product_id_monthly: 'com.app.ai8080.sub.byok.monthly',
    apple_product_id_annual: 'com.app.ai8080.sub.byok.annual',
    apple_price_monthly: 7.49,
    apple_price_annual: 79.99,
    features: [...SHARED_FEATURES, 'BYOK Support', 'Unlimited Prompts'],
  },
  plus: {
    name: 'Plus',
    monthly_price: 25,
    annual_price: 270,
    monthly_credits: 2500,
    monthly_total_credits: 5000,
    daily_drip_credits: 86.21,
    max_users: -1,
    topup_price_per_credit: 0.005,
    topup_price_per_credit_inr: 0.448,
    topup_price_per_credit_apple: 0.007143,
    monthly_price_inr: 2240,
    annual_price_inr: 24192,
    apple_product_id_monthly: 'com.app.ai8080.sub.plus.monthly.v1',
    apple_product_id_annual: 'com.app.ai8080.sub.plus.annual.v1',
    apple_price_monthly: 35.99,
    apple_price_annual: 389.99,
    features: [...SHARED_FEATURES, 'BYOK Support'],
  },
  pro: {
    name: 'Pro',
    monthly_price: 250,
    annual_price: 2700,
    monthly_credits: 25000,
    monthly_total_credits: 50000,
    daily_drip_credits: 862.07,
    max_users: -1,
    topup_price_per_credit: 0.005,
    topup_price_per_credit_inr: 0.448,
    topup_price_per_credit_apple: 0.007143,
    monthly_price_inr: 22400,
    annual_price_inr: 241920,
    features: [...SHARED_FEATURES, 'BYOK Support'],
  },
  unlimited: {
    name: 'Unlimited',
    monthly_price: 1499,
    annual_price: null,
    monthly_credits: -1,
    daily_drip_credits: 0,
    max_users: -1,
    topup_price_per_credit: null,
    topup_price_per_credit_inr: null,
    topup_price_per_credit_apple: null,
    monthly_price_inr: 140000,
    annual_price_inr: null,
    is_unlimited: true,
    topups_allowed: false,
    apple_available: false,
    organization_unlimited: true,
    features: [
      ...SHARED_FEATURES,
      'BYOK Support',
      'Unlimited Credits',
      'Unlimited Users',
      'Unlimited Projects',
    ],
  },
};
